/**
 * Timezone-safe date formatting utilities.
 *
 * The core problem: `new Date("2026-02-01")` (date-only string) is parsed as
 * UTC midnight per the ECMAScript spec. In western US timezones (e.g. PST = UTC-8),
 * that becomes Jan 31 at 4:00 PM local — shifting the displayed date back one day.
 *
 * `parseDateSafe` fixes this by detecting date-only strings and constructing the
 * Date in local time instead.
 *
 * NOTE: For monitoring/billing flows where day boundaries depend on the
 * client's business timezone, prefer the location-TZ helpers at the bottom
 * of this file (`toUTCISOString`, `formatInLocationTZ`, `getLocationYearMonth`).
 * `formatLocalISOString` and `formatNaiveDateTime` are kept for backward compat
 * with existing callers but treat the writer's browser clock as authoritative,
 * which is incorrect across timezones.
 */

import dayjs from "./dayjs-setup";

/**
 * Parse a date input safely, avoiding the UTC-midnight timezone shift for
 * date-only strings like "2026-02-01".
 *
 * - Date-only strings (no T or Z): split and create local-time Date
 * - ISO datetime strings (contain T/Z): use native Date parsing (correct)
 * - Date objects: returned as-is
 * - null/undefined/invalid: returns null
 */
export function parseDateSafe(input) {
  if (!input) return null;
  if (input instanceof Date) return isNaN(input.getTime()) ? null : input;

  if (typeof input === "string") {
    // If the string contains T or Z, it's an ISO datetime — parse normally
    if (input.includes("T") || input.includes("Z")) {
      const d = new Date(input);
      return isNaN(d.getTime()) ? null : d;
    }

    // Date-only string (e.g. "2026-02-01") — parse as local time
    const parts = input.split("-").map(Number);
    if (parts.length >= 3 && parts.every(n => !isNaN(n))) {
      const [year, month, day] = parts;
      return new Date(year, month - 1, day);
    }

    // Fallback: try native parsing
    const d = new Date(input);
    return isNaN(d.getTime()) ? null : d;
  }

  return null;
}

/**
 * Format as MM/DD/YYYY (e.g. "02/01/2026")
 */
export function formatUSADate(input) {
  const d = parseDateSafe(input);
  if (!d) return "--";
  const mm = (d.getMonth() + 1).toString().padStart(2, "0");
  const dd = d.getDate().toString().padStart(2, "0");
  return `${mm}/${dd}/${d.getFullYear()}`;
}

/**
 * Format as MM/DD/YYYY h:mm AM/PM (e.g. "02/01/2026 3:45 PM")
 */
export function formatUSADateTime(input) {
  const d = parseDateSafe(input);
  if (!d) return "--";

  const mm = (d.getMonth() + 1).toString().padStart(2, "0");
  const dd = d.getDate().toString().padStart(2, "0");
  const datePart = `${mm}/${dd}/${d.getFullYear()}`;

  let hours = d.getHours();
  const minutes = d.getMinutes().toString().padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12 || 12;

  return `${datePart} ${hours}:${minutes} ${ampm}`;
}

/**
 * Format as MM/DD/YYYY, h:mm:ss AM/PM (e.g. "02/01/2026, 3:45:30 PM")
 * Uses toLocaleString for the full timestamp format.
 */
export function formatUSATimestamp(input) {
  const d = parseDateSafe(input);
  if (!d) return "--";
  return d.toLocaleString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  });
}

/**
 * Format as "February 2026" (Month Year) from a "YYYY-MM" or "YYYY-MM-DD" string.
 */
export function formatReportMonth(input) {
  if (!input) return "";
  const parts = input.split("-").map(Number);
  if (parts.length < 2 || parts.some(isNaN)) return "";
  const [year, month] = parts;
  const dateObj = new Date(year, month - 1, 1);
  return dateObj.toLocaleDateString("en-US", {
    month: "long",
    year: "numeric",
  });
}

/**
 * Format as MM-DD-YYYY (e.g. "02-01-2026")
 */
export function formatUSADateDash(input) {
  const d = parseDateSafe(input);
  if (!d) return "--";
  const mm = (d.getMonth() + 1).toString().padStart(2, "0");
  const dd = d.getDate().toString().padStart(2, "0");
  return `${mm}-${dd}-${d.getFullYear()}`;
}

/**
 * Format as YYYY-MM-DD date-only string (e.g. "2026-02-01").
 * Returns "N/A" for null/invalid input.
 */
export function formatDateOnly(input) {
  const d = parseDateSafe(input);
  if (!d) return "N/A";
  const yyyy = d.getFullYear();
  const mm = (d.getMonth() + 1).toString().padStart(2, "0");
  const dd = d.getDate().toString().padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

/**
 * Format as MM-DD-YYYY h:mm AM/PM (e.g. "02-01-2026 3:45 PM")
 * Uses 12-hour AM/PM format.
 */
export function formatUSADateTimeDash(input) {
  const d = parseDateSafe(input);
  if (!d) return "N/A";
  const mm = (d.getMonth() + 1).toString().padStart(2, "0");
  const dd = d.getDate().toString().padStart(2, "0");
  let hours = d.getHours();
  const minutes = d.getMinutes().toString().padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12 || 12;
  return `${mm}-${dd}-${d.getFullYear()} ${hours}:${minutes} ${ampm}`;
}

/**
 * Format as "MM-DD-YYYY HH:MM AM/PM" with zero-padded hours
 * (e.g. "02-23-2026 03:35 PM"). Matches Figma 357:1621 for the
 * patient-detail "Last called" header pill.
 */
export function formatLastCalledHeader(input) {
  const d = parseDateSafe(input);
  if (!d) return "--";
  const mm = (d.getMonth() + 1).toString().padStart(2, "0");
  const dd = d.getDate().toString().padStart(2, "0");
  let hours = d.getHours();
  const minutes = d.getMinutes().toString().padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12 || 12;
  const strHours = hours.toString().padStart(2, "0");
  return `${mm}-${dd}-${d.getFullYear()} ${strHours}:${minutes} ${ampm}`;
}

/**
 * Format as "Jan 1, 2026 02:30 PM" (abbreviated month with 12-hour time).
 * Used for "Last Call" columns across My Tasks and dashboard pages.
 */
export function formatLastCallDate(input) {
  const d = parseDateSafe(input);
  if (!d) return "--";

  const months = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
  ];

  const month = months[d.getMonth()];
  const day = d.getDate();
  const year = d.getFullYear();

  let hours = d.getHours();
  const minutes = d.getMinutes().toString().padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";

  hours = hours % 12;
  hours = hours ? hours : 12;
  const strHours = hours.toString().padStart(2, "0");

  return `${month} ${day}, ${year} ${strHours}:${minutes} ${ampm}`;
}

/**
 * Format a Date object as a local-time ISO string without the Z suffix.
 * This preserves the user's local date/time components so that backend
 * month/year filtering matches the user's intended date.
 *
 * Example: Feb 28 2026, 7:00 PM PST → "2026-02-28T19:00:00"
 * (instead of .toISOString() which would give "2026-03-01T03:00:00.000Z")
 */
export function formatLocalISOString(date) {
  if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
    return new Date().toISOString();
  }
  const yyyy = date.getFullYear();
  const mm = (date.getMonth() + 1).toString().padStart(2, "0");
  const dd = date.getDate().toString().padStart(2, "0");
  const hh = date.getHours().toString().padStart(2, "0");
  const min = date.getMinutes().toString().padStart(2, "0");
  const ss = date.getSeconds().toString().padStart(2, "0");
  return `${yyyy}-${mm}-${dd}T${hh}:${min}:${ss}`;
}

/**
 * Format an ISO datetime string for display WITHOUT timezone conversion.
 * Parses date/time components directly from the string so the displayed
 * value always matches what the user originally entered, regardless of
 * the viewer's timezone.
 *
 * Use this for user-reported timestamps (e.g. monitoring session dates)
 * where the stored value represents the creator's local time, not a UTC event.
 *
 * Example: "2026-02-28T19:00:00Z" → "Feb 28, 2026, 07:00:00 PM"
 */
export function formatNaiveDateTime(isoString) {
  if (!isoString) return "--";

  // Strip Z suffix or timezone offset, extract components from the string directly
  const clean = isoString.replace(/Z$/, "").replace(/[+-]\d{2}:\d{2}$/, "");
  const match = clean.match(
    /(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})/
  );
  if (!match) return "--";

  const [, year, month, day, hours, minutes, seconds] = match;
  const months = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
  ];
  const monthName = months[parseInt(month) - 1];

  let h = parseInt(hours);
  const ampm = h >= 12 ? "PM" : "AM";
  h = h % 12 || 12;
  const hStr = h.toString().padStart(2, "0");

  return `${monthName} ${parseInt(day)}, ${year}, ${hStr}:${minutes}:${seconds} ${ampm}`;
}

/**
 * Get ordinal suffix for a day number (st, nd, rd, th).
 */
export function getOrdinalSuffix(day) {
  if (day > 3 && day < 21) return "th";
  const lastDigit = day % 10;
  return lastDigit === 1
    ? "st"
    : lastDigit === 2
      ? "nd"
      : lastDigit === 3
        ? "rd"
        : "th";
}

// ─────────────────────────────────────────────────────────────────────────────
// Location-timezone helpers
//
// The active location carries an IANA timezone (e.g. "America/New_York") that
// represents the business clock for that tenant. Use these helpers when the
// stored value should be a universal moment (UTC) and the displayed value
// should reflect the location's wall-clock — independent of the viewer's or
// creator's browser timezone. This is the correct pattern for daily aggregates
// and any data where the day-boundary matters (RPM/CCM monitoring, billing).
// ─────────────────────────────────────────────────────────────────────────────

const FALLBACK_TZ = "America/New_York";

/**
 * Read the active location's IANA timezone from sessionStorage.
 * Falls back to America/New_York if missing or unparseable.
 */
export function getLocationTimezone() {
  try {
    const loc = JSON.parse(sessionStorage.getItem("current_loc"));
    return loc?.timezone || FALLBACK_TZ;
  } catch {
    return FALLBACK_TZ;
  }
}

/**
 * Convert any date-like value (Date, ISO string, dayjs, or null/undefined)
 * to a UTC ISO string with Z suffix. Send this in `created_at` payloads so
 * the backend stores a universal moment that any timezone can render correctly.
 *
 * Example: `Date` representing Apr 30 8:00 PM EST → "2026-05-01T00:00:00.000Z"
 */
export function toUTCISOString(input) {
  if (input === null || input === undefined || input === "") {
    return dayjs().utc().toISOString();
  }
  return dayjs(input).utc().toISOString();
}

/**
 * Sessions written before this moment used `formatLocalISOString`, which
 * captured the writer's browser wall-clock components and shipped them as a
 * naive string that the backend persisted with a `Z` appended. Reinterpreting
 * those values as UTC and converting them to the location's timezone shifts
 * the displayed wall-clock by the location's UTC offset (e.g. -8h for PST).
 * For pre-cutoff timestamps we fall back to `formatNaiveDateTime` so historical
 * rows render exactly as they did before the TZ fix shipped.
 *
 * Cutoff = the earliest moment any environment received the TZ fix
 * (PR #232 merged at 2026-05-01T11:55:44Z).
 */
const TZ_FIX_CUTOFF = "2026-05-01T11:55:44Z";

/**
 * Format a UTC ISO string for display in the active location's timezone.
 * Default format matches the legacy `formatNaiveDateTime` shape so visual
 * regressions are minimized: "Feb 28, 2026, 07:00:00 PM".
 *
 * Pre-cutoff values fall back to `formatNaiveDateTime` (see TZ_FIX_CUTOFF).
 */
export function formatInLocationTZ(
  isoString,
  formatStr = "MMM D, YYYY, hh:mm:ss A"
) {
  if (!isoString) return "--";
  if (isoString < TZ_FIX_CUTOFF) {
    return formatNaiveDateTime(isoString);
  }
  const tz = getLocationTimezone();
  return dayjs.utc(isoString).tz(tz).format(formatStr);
}

/**
 * Current { year, month } in the active location's timezone, both as
 * zero-padded strings ("2026", "05") to match how `monitoringTime.js` uses
 * them as dropdown values and query params.
 */
export function getLocationYearMonth() {
  const tz = getLocationTimezone();
  const now = dayjs().tz(tz);
  return {
    year: now.format("YYYY"),
    month: now.format("MM"),
  };
}
